<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <label class="label-input">Layout</label>
          <div class="group-item">
            <v-text-field
              type="number"
              v-model.number="fields.layout.gap"
              label="Gap"
              min="0"
              hide-details
              suffix="px"
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Image</label>
          <div class="group-item">
            <v-select
              v-model="fields.image.position"
              label="Position (Desktop)"
              :items="positionOptions"
              item-text="text"
              item-value="value"
              hide-details
              class="mb-4"
            />
            <UploadImage
              @callback="handleUploadImage"
              :image="{ imageSrc: fields.image.src }"
              :height="`200`"
              @callbackDelete="handleRemoveImage"
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Specification</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.specification" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">What's In The Box</label>
          <div class="group-item">
            <v-text-field v-model="fields.box.title" label="Title" hide-details class="mb-4" />
            <v-row class="mb-4">
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.box.titleBackgroundColor"
                  name="boxTitleBackgroundColor"
                  label="Background color"
                  default-color="#d35400"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.box.titleTextColor"
                  name="boxTitleTextColor"
                  label="Text color"
                  default-color="#ffffff"
                />
              </v-col>
            </v-row>
            <div class="d-flex justify-end mb-2">
              <v-btn small text color="primary" @click="handleAddItem">
                <v-icon size="16">mdi-plus</v-icon>
                <span>Add Item</span>
              </v-btn>
            </div>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.box.items" :key="`box-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span class="line-clamp-1">{{ item || `Item ${index + 1}` }}</span>
                    <v-btn icon @click.stop="handleRemoveItem(index)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field v-model="fields.box.items[index]" label="Item text" hide-details />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update.js';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import ColorPicker from './common/color-picker.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const defaultBoxItems = () => [
  'Magnifier × 1',
  'Fabric Pouch × 1',
  'Lens Cleaning Cloth × 1',
  'USB-C Charging Cable × 1',
  'User Manual × 1',
];

export default {
  name: 'sowhatbuy-card-basic-section-editor',
  components: {
    SectionSettingsContainerFuild,
    UploadImage,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      positionOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Right', value: 'right' },
      ],
      fields: {
        title: 'Sowhatbuy card basic',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#F8FAF5',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        layout: {
          gap: 40,
        },
        image: {
          src: '',
          position: 'left',
        },
        specification: '',
        box: {
          title: "WHAT'S IN THE BOX",
          titleBackgroundColor: '#d35400',
          titleTextColor: '#ffffff',
          items: defaultBoxItems(),
        },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        layout: { ...this.fields.layout, ...(extract.layout || {}) },
        image: { ...this.fields.image, ...(extract.image || {}) },
        box: {
          ...this.fields.box,
          ...(extract.box || {}),
          items: Array.isArray(extract.box?.items) ? [...extract.box.items] : this.fields.box.items,
        },
      };
    },
    handleUploadImage(obj) {
      if (!obj || !obj.imageSrc) return;
      this.fields.image.src = obj.imageSrc;
    },
    handleRemoveImage() {
      this.fields.image.src = '';
    },
    handleAddItem() {
      this.fields.box.items.push('New item');
    },
    handleRemoveItem(index) {
      this.fields.box.items.splice(index, 1);
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionSowhatbuyCardBasic(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
::v-deep .ck-editor__editable_inline {
  min-height: 120px !important;
}
</style>
